import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CheckCircle2, ShieldCheck, MapPin, BookOpen, FileText, ArrowRight,
  Loader2, Mail, ScrollText, AlertTriangle, Gavel, Download,
} from "lucide-react";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { injectJsonLd, removeJsonLd } from "@/lib/seo";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { setUserRole } from "@/lib/userRole";
import { getAbVariant } from "@/lib/abTest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAGNET_ID = "wer-darf-was-atlas";
const KURZ_URL = `${API}/leadmagnet/${MAGNET_ID}/kurzversion`;

// Instant-Download der Kurzversion (kein E-Mail-Gate) · nur GA-Event,
// KEIN Google-Ads-Lead-Conversion (würde die Lead-Statistik verwässern).
function trackKurzDownload(bl, variant, spot) {
  try {
    window.gtag?.("event", "kurzversion_download", {
      lead_source: `ads-lp-wer-darf-was:${bl}:${variant}:${spot}`,
    });
  } catch { /* noop */ }
}

// 16 Bundesländer · Code + Kurzname für den BL-Wähler
const BL = [
  ["BW", "Baden-Württemberg"], ["BY", "Bayern"], ["BE", "Berlin"], ["BB", "Brandenburg"],
  ["HB", "Bremen"], ["HH", "Hamburg"], ["HE", "Hessen"], ["MV", "Mecklenburg-Vorpommern"],
  ["NI", "Niedersachsen"], ["NW", "Nordrhein-Westfalen"], ["RP", "Rheinland-Pfalz"],
  ["SL", "Saarland"], ["SN", "Sachsen"], ["ST", "Sachsen-Anhalt"], ["SH", "Schleswig-Holstein"],
  ["TH", "Thüringen"],
];

// Strenge-Signal pro BL (qualitatives Indiz, basierend auf den Rahmenverträgen
// laut atlas_bundeslaender.py – hier nur als Such-Hook). Drei Stufen.
const STRENGE = {
  BY: "streng", BW: "mittel", BE: "mittel", BB: "mittel", HB: "mittel", HH: "streng",
  HE: "streng", MV: "mittel", NI: "mittel", NW: "mittel", RP: "mittel", SL: "mittel",
  SN: "mittel", ST: "mittel", SH: "mittel", TH: "mittel",
};

// FAQ – beantwortet die häufigsten Google-Suchen direkt und liefert
// Rich-Snippets via JSON-LD.
const FAQ = [
  ["Wer darf was im ambulanten Pflegedienst?",
   "Es gibt drei Ebenen: 1) Pflegefachkräfte (Examen) dürfen praktisch alle Behandlungspflege-Leistungen ausführen. 2) Pflegeassistent:innen mit LG1/LG2-Qualifikation übernehmen klar definierte Behandlungspflege-Tätigkeiten (z. B. Medikamentengabe, s.c.-/i.m.-Injektionen, Verbandwechsel, Kompression). 3) Pflegehilfskräfte ohne LG-Schulung sind auf Grundpflege beschränkt. Welche Tätigkeit konkret an wen delegierbar ist, regelt der jeweilige Landes-Rahmenvertrag – diese Antwort variiert zwischen den 16 Bundesländern. Der Delegations-Atlas listet jede der 42 Tätigkeiten mit einer 16-Bundesländer-Schnellmatrix und 4-Label-Pre-Flight-Checkliste (Gesetz · Landesvertrag · MD-QM · Praxistipp)."],
  ["Welche Tätigkeiten darf eine Pflegehilfskraft nach LG1/LG2 übernehmen?",
   "LG1 deckt u. a. Medikamentengabe (auch BtM nach 4-Augen-Prinzip), Blutdruck-/Blutzuckermessung, s.c.-Injektionen (z. B. Insulin) und Kompressionsstrümpfe ab. LG2 erweitert um i.m.-Injektionen, Verbandwechsel, transurethralen Katheterismus, einfache Wundversorgung und Kompressionsbandagen. Jeder dieser Tätigkeiten hat im Atlas eine eigene Seite mit Pre-Flight-Checkliste, 16-Bundesländer-Matrix und 7 didaktischen Lehrfällen auf Grundlage obergerichtlicher Rechtsprechungsgrundsätze."],
  ["Darf die Hilfskraft die Medikamenten-Wochenbox stellen?",
   "Die Antwort hängt vom Bundesland und der Qualifikation ab. Grundsatz: Das Richten/Stellen ist behandlungspflegerische Vorbereitung. In den meisten Bundesländern darf das eine Pflegefachkraft oder eine LG1/LG2-qualifizierte Assistenzkraft – nicht eine ungeschulte Hilfskraft. BtM-Stellen ist in Bayern und Hamburg besonders kritisch geregelt. Der Atlas hat dazu Tätigkeit A1 (Stellen) komplett ausgearbeitet mit Pre-Flight, Mustertexten und Eskalations-Brief Pflege→Arzt."],
  ["Wer haftet, wenn die Pflegekraft eine falsch delegierte Tätigkeit ausführt?",
   "Drei Ebenen: 1) Die ausführende Pflegekraft (Übernahmeverschulden bei fehlender Qualifikation), 2) die delegierende Pflegefachkraft/PDL (Auswahl-/Anleitungs-/Überwachungsverschulden), 3) der Pflegedienst als Arbeitgeber (Organisationsverschulden). Im Schadensfall prüfen MD und Gerichte: gab es eine schriftliche Delegationsanordnung? Qualifikationsnachweis? Anleitung dokumentiert? Der Atlas enthält pro Tätigkeit eine 7-Fall-Haftungsliste plus Mustertexte für die rechtssichere Delegation."],
  ["Ist der Atlas kostenlos?",
   "Die 17-Seiten-Vorschau ist kostenlos und kommt sofort per E-Mail. Die komplette Vollversion (251 Seiten, 42 Tätigkeiten, 16-Bundesländer-Matrix, Mustertexte) gibt es als Fachbuch-PDF für 49,99 € in unserem Shop – einmal kaufen, dauerhaft deins, DRM-frei. Wer das gedruckte Buch bevorzugt: Der Atlas erscheint auch als Printausgabe bei Amazon."],
];

export default function LpWerDarfWas() {
  useLpMeta(
    "Wer darf was im Pflegedienst? · Delegations-Atlas · alle 16 BL · 17-Seiten-Vorschau gratis | Dickies-Campus",
    "Klare Antwort für jede der 42 Behandlungspflege-Tätigkeiten in deinem Bundesland: 16-BL-Matrix, 4-Label-Pre-Flight, Haftungsfallen, Mustertexte. 17-Seiten-Vorschau gratis, Vollversion (251 S.) als Fachbuch-PDF 49,99 €."
  );
  const [bl, setBl] = useState("NW");
  const [variant] = useState(() => getAbVariant());
  const isB = variant === "B";
  const [form, setForm] = useState({ name: "", email: "", role: "pflegekraft", company: "", position: "", phone: "", bundesland: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    injectJsonLd("ld-faq-wer-darf-was", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ.map(([q, a]) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
      })),
    });
    return () => removeJsonLd("ld-faq-wer-darf-was");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/leadmagnet/${MAGNET_ID}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        company: form.company,
        position: form.position,
        phone: form.phone,
        bundesland: form.bundesland || bl,
        consent: true,
        newsletter_opt_in: false,
        source: `ads-lp-wer-darf-was:${bl}:${variant}`,
        attribution: getAttribution(),
      });
      trackLeadConversion(5, `leadmagnet:${MAGNET_ID}:${variant}`);
      metaLead(`leadmagnet:${MAGNET_ID}`);
      setUserRole(form.role);
      setDone(res.data);
      // DOI: kein direkter Download mehr
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(
        status === 400 && typeof detail === "string" ? detail
        : status === 422 ? "Bitte gültige E-Mail eingeben."
        : "Bitte später erneut versuchen."
      );
    }
    setSubmitting(false);
  };

  const blName = BL.find(([c]) => c === bl)?.[1] ?? "";
  const strenge = STRENGE[bl] ?? "mittel";
  const strengeBadge = strenge === "streng"
    ? { c: "bg-brand-darkred text-white", t: "MD-Hotspot · MD prüft besonders kritisch" }
    : { c: "bg-sand-800 text-cream-50", t: "Mittlere Prüfdichte · Standard-Rahmenvertrag" };

  return (
    <LpShell ctaLabel={isB ? "JETZT Vorschau sichern" : "Vorschau gratis anfordern"} ctaHref="#lp-pdf">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-start">
          <div>
            {isB ? (
              <div data-testid="lp-wdw-prehead-b" className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-white px-4 h-8 text-[10.5px] uppercase tracking-[0.22em] font-bold">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.2} /> Warnung an PDLs · MD-Prüfung 2026
              </div>
            ) : (
              <GoogleStarsBadge />
            )}
            <h1 data-testid="lp-wdw-headline" className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-normal tracking-tight text-sand-800 leading-[1.05]">
              {isB ? (
                <>
                  8 Tätigkeiten,<br />
                  die deine Hilfskraft<br />
                  <span className="text-brand-darkred">NICHT machen darf —</span><br />
                  <span className="text-terra">der MD prüft sie zuerst.</span>
                </>
              ) : (
                <>
                  Wer darf was<br />
                  im Pflegedienst?<br />
                  <span className="text-terra">Die ehrliche Antwort. 251 Seiten. Vorschau gratis.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-sand-600 text-base md:text-lg leading-[1.6] max-w-xl">
              {isB ? (
                <>
                  In 2025 haben Pflegedienste in <strong className="text-sand-800">jedem zweiten MD-Bericht</strong> Punkte wegen falscher Delegation verloren.
                  Eine Hilfskraft stellt die Wochenbox in Bayern? <strong className="text-brand-darkred">Sofortige Abwertung.</strong>{" "}
                  Welche Tätigkeit in deinem Bundesland zur Haftungsfalle wird, weißt du in 60 Sekunden.
                </>
              ) : (
                <>
                  Praxisanleiterin sagt „Mach mal“. MD-Prüfer fragt „Wer hat das freigegeben?“.
                  Tochter ruft an. <strong className="text-sand-800">Drei Situationen, drei Rechtsgrundlagen, 16 mögliche Antworten</strong> –
                  je nach Bundesland.
                </>
              )}
            </p>
            <p className="mt-4 text-sand-600 text-[15px] leading-[1.65] max-w-xl">
              {isB ? (
                <>
                  Der <strong>200+-Seiten-Atlas</strong> listet alle <strong>42 Behandlungspflege-Tätigkeiten</strong> gegen alle{" "}
                  <strong>16 Landes-Rahmenverträge</strong> auf — mit MD-Hotspot-Markierung und didaktischen Lehrfällen auf Grundlage obergerichtlicher Rechtsprechungsgrundsätze
                  und Eskalations-Brief Pflege → Arzt zum sofortigen Anpassen. <strong className="text-brand-darkred">0 € · keine Bezahlschranke.</strong>
                </>
              ) : (
                <>
                  Der Delegations-Atlas ordnet die Regelungen systematisch: <strong>42 Behandlungspflege-Tätigkeiten</strong> ·
                  <strong> 16-Bundesländer-Schnellmatrix</strong> · 4-Label-Pre-Flight (Gesetz · Landesvertrag · MD-QM · Praxistipp) ·
                  7 Haftungsfallen pro Tätigkeit · Mustertexte aus reiner Pflege-Perspektive.
                </>
              )}
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              {[
                { icon: BookOpen, t: "42 Behandlungspflege-Tätigkeiten" },
                { icon: MapPin, t: "Alle 16 Bundesländer geprüft" },
                { icon: ShieldCheck, t: isB ? "MD-Hotspot-Markierung pro Tätigkeit" : "4-Label-System (Gesetz · LV · MD · Praxis)" },
                { icon: FileText, t: "200+ Seiten · sofort als PDF im Postfach" },
              ].map(({ icon: Icon, t }) => (
                <li key={t} className="flex items-center gap-2.5 text-[14px] text-sand-700">
                  <Icon className="h-5 w-5 text-terra shrink-0" strokeWidth={1.7} /> {t}
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#lp-pdf" data-testid="lp-wdw-cta-pdf"
                className={`inline-flex items-center gap-2 rounded-full ${isB ? "bg-brand-darkred hover:bg-[#3f1f1c]" : "bg-terra hover:bg-terra-dark"} text-white px-7 py-3.5 text-base font-semibold transition-colors group`}>
                {isB ? "JETZT Vorschau sichern (gratis)" : "Vorschau gratis anfordern"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
              </a>
              <a href="#lp-bl" data-testid="lp-wdw-cta-bl"
                className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 py-3.5 text-base font-medium transition-colors">
                {isB ? "Mein Bundesland prüfen" : "Mein Bundesland anschauen"}
              </a>
            </div>
          </div>

          {/* Lead-Box · Hero-side */}
          <aside id="lp-pdf" className="lg:sticky lg:top-24">
            <div className="rounded-3xl bg-white border border-cream-300 p-7 sm:p-9 shadow-soft">
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-brand-darkred">
                Lead-Magnet · 0 € · sofort
              </div>
              <h2 className="mt-3 font-display text-2xl sm:text-[1.7rem] text-sand-800 leading-tight">
                {isB ? "60 Sekunden bis zur MD-Sicherheit." : "Atlas direkt anfordern – ein Klick, eine E-Mail."}
              </h2>
              {done ? (
                <div data-testid="lp-wdw-success" className="mt-6 rounded-2xl bg-sage-light/40 border border-sage-dark/20 p-5 text-center">
                  <Mail className="h-10 w-10 text-sage-dark mx-auto mb-3" strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-sand-800">Fast geschafft!</h3>
                  <p className="text-sand-600 text-[13.5px] mt-2 leading-relaxed">
                    Wir haben dir eine Bestätigungs-Mail an <strong>{form.email}</strong> geschickt. Klicke darin auf den Link – danach öffnet sich der Atlas sofort.
                  </p>
                  <p className="mt-3 text-[12px] text-sand-500">Kein Mail erhalten? Bitte auch Spam-Ordner prüfen.</p>
                  <a href={KURZ_URL} target="_blank" rel="noreferrer"
                    data-testid="lp-wdw-kurz-download-success"
                    onClick={() => trackKurzDownload(bl, variant, "success")}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-900 text-white px-5 h-11 text-[13px] font-semibold transition-colors">
                    <Download className="h-4 w-4" strokeWidth={2} />
                    Während du wartest: Kurzversion sofort lesen (14 S.)
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="lp-wdw-form" className="mt-5 space-y-3.5">
                  <Input data-testid="lp-wdw-name" required minLength={2} placeholder="Name *"
                    className="h-12 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra"
                    value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                  <Input data-testid="lp-wdw-email" type="email" required placeholder="E-Mail *"
                    className="h-12 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra"
                    value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <Input data-testid="lp-wdw-company" placeholder="Pflegedienst / Klinik (optional)"
                      className="h-12 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra"
                      value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
                    <Input data-testid="lp-wdw-position" placeholder="Position (optional)"
                      className="h-12 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra"
                      value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} />
                  </div>
                  <Input data-testid="lp-wdw-phone" type="tel" inputMode="tel" required placeholder="Telefon (z. B. 0211 12345678) *"
                    className="h-12 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra"
                    value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ["pflegekraft", "Pflegekraft"],
                      ["pdl", "PDL / Leitung"],
                      ["angehoerig", "Angehörige:r"],
                    ].map(([v, label]) => (
                      <button type="button" key={v}
                        data-testid={`lp-wdw-role-${v}`}
                        onClick={() => setForm((f) => ({ ...f, role: v }))}
                        className={`rounded-xl h-11 text-[12.5px] font-medium border transition-colors ${form.role === v ? "bg-sand-800 text-white border-sand-800" : "bg-white text-sand-700 border-cream-300 hover:border-sand-400"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {error && <p className="text-[12.5px] text-brand-darkred">{error}</p>}
                  <Button type="submit" disabled={submitting} data-testid="lp-wdw-submit"
                    className={`w-full rounded-full ${isB ? "bg-brand-darkred hover:bg-[#3f1f1c]" : "bg-terra hover:bg-terra-dark"} text-white h-12 text-[14.5px] font-semibold group`}>
                    {submitting
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><Mail className="h-4 w-4 mr-2" strokeWidth={1.8} /> {isB ? "Vorschau JETZT sichern" : "Vorschau an mich schicken"} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} /></>}
                  </Button>
                  <p className="text-[11px] text-sand-500 leading-relaxed">
                    Vorschau (17 Seiten) kostenlos · DSGVO-konform · Widerruf jederzeit per Mail. Die Vollversion (251 S.) gibt es als Fachbuch-PDF im Shop (49,99 €).
                  </p>
                  <div className="pt-3 mt-1 border-t border-cream-300">
                    <a href={KURZ_URL} target="_blank" rel="noreferrer"
                      data-testid="lp-wdw-kurz-download"
                      onClick={() => trackKurzDownload(bl, variant, "form")}
                      className="flex items-center justify-between gap-3 rounded-2xl bg-cream-50 border border-cream-300 hover:border-terra/50 px-4 py-3 transition-colors group">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-sand-800 leading-tight">
                          Noch unsicher? Kurzversion sofort ansehen
                        </div>
                        <div className="text-[11px] text-sand-500 mt-0.5">
                          14 Seiten · Top-10-Tätigkeiten · ohne E-Mail, direkt als PDF
                        </div>
                      </div>
                      <Download className="h-4 w-4 text-terra shrink-0 transition-transform group-hover:translate-y-0.5" strokeWidth={2} />
                    </a>
                  </div>
                </form>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Was steckt drin · 4-Label-System */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-3">Das 4-Label-Pre-Flight-System</h2>
          <p className="text-sand-600 text-[14.5px] max-w-2xl mb-9">
            Jede der 42 Tätigkeiten beantworten wir auf vier Ebenen. Drei Sekunden Blickzeit – und du weißt, was rechtssicher geht.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Gavel, k: "Gesetz", c: "Bundesrecht: SGB V/XI, BtMVV, Apothekengesetz – was gilt überall.", color: "bg-sand-800 text-cream-50" },
              { icon: ScrollText, k: "Landesvertrag", c: "Der konkrete Rahmenvertrag deines Bundeslandes (16 BL geprüft).", color: "bg-terra text-white" },
              { icon: ShieldCheck, k: "MD-QM", c: "Was prüft der MD wirklich? Was hat schon zu Abwertungen geführt?", color: "bg-brand-darkred text-white" },
              { icon: CheckCircle2, k: "Helden-Praxistipp", c: "Was wir empfehlen, wenn die anderen drei Ebenen sich widersprechen.", color: "bg-sage-dark text-white" },
            ].map(({ icon: Icon, k, c, color }) => (
              <div key={k} className="rounded-3xl bg-white border border-cream-300 p-6 flex flex-col">
                <span className={`self-start inline-flex items-center gap-1.5 rounded-full px-3 h-7 text-[11px] font-bold uppercase tracking-[0.18em] ${color}`}>
                  <Icon className="h-3 w-3" strokeWidth={2.2} /> {k}
                </span>
                <p className="mt-4 text-[13.5px] text-sand-700 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundesländer-Wähler */}
      <section id="lp-bl" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-3xl text-sand-800 mb-3">
          {isB ? "Klick dein Bundesland — sieh, wo dir die MD-Punkte fehlen." : "Wie streng ist dein Bundesland?"}
        </h2>
        <p className="text-sand-600 text-[14.5px] max-w-2xl mb-9">
          Klick auf dein Bundesland – wir zeigen dir vorab, welcher Rahmenvertrag gilt und wie streng der MD dort prüft.
          Die Detail-Antwort pro Tätigkeit steht im Atlas.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-7">
          {BL.map(([code, name]) => (
            <button type="button" key={code}
              data-testid={`lp-wdw-bl-${code}`}
              onClick={() => setBl(code)}
              className={`rounded-2xl px-2 py-3 text-[12px] font-semibold border transition-all ${bl === code ? "bg-terra text-white border-terra shadow-soft -translate-y-0.5" : "bg-white text-sand-700 border-cream-300 hover:border-sand-400"}`}
              title={name}>
              {code}
            </button>
          ))}
        </div>
        <div data-testid="lp-wdw-bl-detail" className="rounded-3xl bg-white border border-cream-300 p-7 sm:p-9 shadow-soft">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-terra">Bundesland · {bl}</div>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl text-sand-800">{blName}</h3>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-[12px] font-bold uppercase tracking-[0.16em] ${strengeBadge.c}`}>
              <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} /> {strengeBadge.t}
            </span>
          </div>
          <p className="mt-5 text-sand-700 text-[14.5px] leading-relaxed max-w-3xl">
            Im Atlas findest du für <strong>{blName}</strong> eine eigene Steckbrief-Seite im Kapitel 2 (Stand, Helfer-Regel, Besonderheit, Quelle).
            Plus: pro Tätigkeit das konkrete Detail – z. B. ob die Hilfskraft die Wochenbox stellen darf, ob s.c.-Insulin von LG1 ausgeführt werden darf,
            wie BtM korrekt zu dokumentieren ist. Insgesamt 42 Detailseiten mit 16-BL-Spalte.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#lp-pdf" data-testid={`lp-wdw-bl-cta-${bl}`}
              className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-900 text-white px-6 h-11 text-[13.5px] font-semibold">
              Atlas für {bl} anfordern <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/leitfaden/wer-darf-was-atlas" data-testid="lp-wdw-bl-deep"
              className="inline-flex items-center text-[12.5px] underline underline-offset-4 text-sand-600 hover:text-sand-800">
              Vollständige Atlas-Seite mit allen Trust-Boxen →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">Häufige Fragen</h2>
          <div className="space-y-4" data-testid="lp-wdw-faq">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group bg-white rounded-3xl border border-cream-300 p-6">
                <summary className="cursor-pointer font-semibold text-sand-800 text-[15.5px] list-none flex items-start justify-between gap-4">
                  <span>{q}</span>
                  <span className="text-terra text-xl group-open:rotate-45 transition-transform select-none leading-none">+</span>
                </summary>
                <p className="mt-3 text-[14px] text-sand-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bridge zu LG1/LG2-Kurs (höchster Conversion-Pfad) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="rounded-3xl bg-sand-800 text-cream-50 p-8 sm:p-10 lg:p-12 grid lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-10 items-center">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-peach-200">
              Nach dem Atlas · der logische Schritt
            </div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-[2.1rem] leading-[1.1]">
              Du weißt jetzt, <span className="italic text-peach-200">was darf</span> sein.
              <br />Jetzt mach dich – <span className="italic text-peach-200">oder dein Team –</span> dafür qualifiziert.
            </h2>
            <p className="mt-5 text-cream-100/85 text-[14.5px] leading-[1.65] max-w-xl">
              Die meisten Antworten im Atlas zeigen am Ende auf eine Qualifikation:
              <strong className="text-white"> LG1 &amp; LG2 (Behandlungspflege)</strong>.
              Bei uns: 100 % online live, Start jeden Montag, Bildungsgutschein meist 100 %.
            </p>
          </div>
          <div className="space-y-3">
            <Link to="/lg1-lg2" data-testid="lp-wdw-bridge-lg"
              className="block rounded-2xl bg-terra hover:bg-terra-dark text-white px-6 py-4 transition-colors">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-peach-200">Pflegeassistenz LG1 &amp; LG2</div>
              <div className="font-display text-lg mt-1">6,5 Wochen · ab Montag · meist 100 % gefördert →</div>
            </Link>
            <Link to="/pflegedienste" data-testid="lp-wdw-bridge-b2b"
              className="block rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white px-6 py-4 transition-colors">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-peach-200">Für Pflegedienste</div>
              <div className="font-display text-lg mt-1">Team-Buchung · auf Rechnung · −20 % ab 8 TN →</div>
            </Link>
          </div>
        </div>
      </section>
    </LpShell>
  );
}
